import { makeAutoObservable } from 'mobx';
import { EventData, EventProcessor } from '../Model/Engagement/Earn/EventProcessor';
import { EarnModelProcessResult, createEarnModelProcessResult } from '../Model/Engagement/Earn/EarnModelProcessResult';
import { ActivityLog, getActivityLogDurationInSeconds, newActivityLog, saveActivityLogDirect } from '../Model/Activity/ActivityLog';
import { v4 as UUID } from 'uuid';
import { LocalizationStore } from './System/LocalizationStore';
import { Recipe } from '../Model/Diet/Recipe/Recipe';
import { ArticleEntry } from '../Model/Explore/ArticleEntry';
import { GymCustomerStore } from './GymCustomerStore';
import { ShopUrlResponse, createShopUrlResponse } from '../Model/Engagement/ShopUrlResponse';
import { HttpBackend } from '../Services/Http/HttpBackend';
import { NewsArticle } from '../Model/News/NewsArticle';
import dayjs from 'dayjs';

const DEFAULT_READ_TIME_MINUTES = 1;

function contentReadActivityId(type: 'article' | 'recipe', contentId: string): string {
  const today = dayjs().format('YYYY-MM-DD');
  return `${type}-${contentId}-${today}`;
}

type PointsModalData = {
  points: number;
  coins?: number;
  reason: string;
  onButtonPress?: () => void;
};

function createEngagementStore(gymCustomer: GymCustomerStore, language: LocalizationStore) {
  const store = makeAutoObservable({
    gymCustomer,
    language,
    isEnabled: true,
    isPointsModalVisible: false,
    pointsCollected: 0,
    pointsModalData: null as PointsModalData | null,

    shopUrl(): Promise<ShopUrlResponse> {
      return HttpBackend.get(`/coach-program/engagement/loylogic/shopUrl`).then((res) => createShopUrlResponse(res));
    },

    sendEvent(
      event: EventData,
      onPointsModalClosed?: (result?: EarnModelProcessResult) => void,
    ): Promise<EarnModelProcessResult> {
      return EventProcessor.submitEvent(event)
        .then((res) => (this.enabled ? res : createEarnModelProcessResult()))
        .then((res) => {
          if (onPointsModalClosed) {
            if (res.pointsCollected > 0) {
              this.showPointsModal(res, () => onPointsModalClosed(res));
            } else {
              onPointsModalClosed(res);
            }
          }
          return res;
        })
        .catch(() => {
          onPointsModalClosed && onPointsModalClosed();
          return createEarnModelProcessResult();
        });
    },

    sendSignup(onPointsModalClosed?: (res?: EarnModelProcessResult) => void): Promise<EarnModelProcessResult> {
      if (this.gymCustomer.customer) {
        return this.sendEvent(
          {
            event: 'signup',
            requestId: `customer-${this.gymCustomer.customer.id}`,
          },
          onPointsModalClosed,
        );
      }
      return Promise.resolve(createEarnModelProcessResult());
    },

    sendTrackerConnected(
      provider: string,
      onPointsModalClosed?: (res?: EarnModelProcessResult) => void,
    ): Promise<EarnModelProcessResult> {
      if (this.gymCustomer.customer) {
        return this.sendEvent(
          {
            event: 'tracker_connected',
            requestId: `tracker-${provider}-${this.gymCustomer.customer.id}`,
            data: {
              type: provider,
              dataProvider: 'terra',
            },
          },
          onPointsModalClosed,
        );
      }
      return Promise.resolve(createEarnModelProcessResult());
    },

    sendActivityLogged(
      activityLog: ActivityLog,
      onPointsModalClosed?: (res?: EarnModelProcessResult) => void,
    ): Promise<EarnModelProcessResult> {
      const durationInSeconds = getActivityLogDurationInSeconds(activityLog);
      return this.sendEvent(
        {
          event: 'activity_logged',
          requestId: `activity-${activityLog.id}`,
          eventDateTime: new Date(activityLog.startDate),
          data: {
            activityLogId: activityLog.id,
            type: activityLog.type,
            value: durationInSeconds,
            startDateTime: activityLog.startDate,
            endDateTime: activityLog.endDate,
            durationInSeconds,
            'source.source': activityLog.source.source,
            'source.sourceId': activityLog.source.sourceId,
            'source.sourceType': activityLog.source.sourceType,
          },
        },
        onPointsModalClosed,
      );
    },

    sendFitnessProgramStarted(
      onPointsModalClosed?: (res?: EarnModelProcessResult) => void,
    ): Promise<EarnModelProcessResult> {
      return this.sendEvent(
        {
          event: 'fitness_program_started',
          requestId: UUID(),
        },
        onPointsModalClosed,
      );
    },

    sendMindfulnessProgramStarted(
      onPointsModalClosed?: (res?: EarnModelProcessResult) => void,
    ): Promise<EarnModelProcessResult> {
      return this.sendEvent(
        {
          event: 'mindfulness_program_started',
          requestId: UUID(),
        },
        onPointsModalClosed,
      );
    },

    sendRecipeRead(
      content: Recipe,
      durationSeconds?: number,
      onPointsModalClosed?: (res?: EarnModelProcessResult) => void,
    ): Promise<EarnModelProcessResult> {
      const maxReadTimeSeconds = (content.estimatedReadingMinutes || DEFAULT_READ_TIME_MINUTES) * 60;
      const actualDuration = Math.min(durationSeconds ?? maxReadTimeSeconds, maxReadTimeSeconds);

      const activityLog = newActivityLog({
        id: contentReadActivityId('recipe', content.id),
        type: 'recipe',
        activityId: 'recipe',
        startDate: dayjs().subtract(actualDuration, 'seconds').toISOString(),
        endDate: dayjs().toISOString(),
        data: {
          DURATION: actualDuration,
        },
        linkedData: {
          contentId: content.id,
          contentType: 'recipe',
          name: content.getName(this.language.language),
        },
        source: {
          source: 'kinastic',
          sourceType: 'recipe',
          sourceId: content.id,
        },
      });

      const durationInSeconds = getActivityLogDurationInSeconds(activityLog);

      // Send both the specific 'recipe_read' event and the activity log
      return this.sendEvent(
        {
          event: 'recipe_read',
          requestId: `recipe-${activityLog.id}`,
          eventDateTime: new Date(activityLog.startDate),
          data: {
            activityLogId: activityLog.id,
            type: activityLog.type,
            value: durationInSeconds,
            startDateTime: activityLog.startDate,
            endDateTime: activityLog.endDate,
            durationInSeconds,
            contentId: content.id,
            contentType: 'recipe',
            'source.source': activityLog.source.source,
            'source.sourceId': activityLog.source.sourceId,
            'source.sourceType': activityLog.source.sourceType,
          },
        },
        onPointsModalClosed,
      )
        .then((result) => {
          // Also log as activity if the specific event didn't yield points
          if (result.pointsCollected === 0) {
            return this.sendActivityLogged(activityLog).then((actResult) => {
              saveActivityLogDirect(activityLog).catch((err) => console.error('Failed to save recipe read activity:', err));
              return actResult.pointsCollected > 0 ? actResult : result;
            });
          }
          saveActivityLogDirect(activityLog).catch((err) => console.error('Failed to save recipe read activity:', err));
          return result;
        })
        .catch((err) => {
          console.error('Failed to send recipe read engagement:', err);
          onPointsModalClosed?.();
          return createEarnModelProcessResult();
        });
    },

    sendArticleRead(
      content: ArticleEntry | NewsArticle,
      durationSeconds?: number,
      onPointsModalClosed?: (res?: EarnModelProcessResult) => void,
    ): Promise<EarnModelProcessResult> {
      const maxReadTime = ('readTimeInMinutes' in content && content.readTimeInMinutes > 0)
        ? content.readTimeInMinutes * 60
        : DEFAULT_READ_TIME_MINUTES * 60;

      const actualDuration = Math.min(durationSeconds ?? maxReadTime, maxReadTime);

      const activityLog = newActivityLog({
        id: contentReadActivityId('article', content.id),
        type: 'article',
        activityId: 'article',
        startDate: dayjs().subtract(actualDuration, 'seconds').toISOString(),
        endDate: dayjs().toISOString(),
        data: {
          DURATION: actualDuration,
        },
        linkedData: {
          contentId: content.id,
          contentType: 'article',
          name: content.getName(this.language.language),
        },
        source: {
          source: 'kinastic',
          sourceType: 'article',
          sourceId: content.id,
        },
      });

      const durationInSeconds = getActivityLogDurationInSeconds(activityLog);

      // Send both the specific 'article_read' event and the activity log
      return this.sendEvent(
        {
          event: 'article_read',
          requestId: `article-${activityLog.id}`,
          eventDateTime: new Date(activityLog.startDate),
          data: {
            activityLogId: activityLog.id,
            type: activityLog.type,
            value: durationInSeconds,
            startDateTime: activityLog.startDate,
            endDateTime: activityLog.endDate,
            durationInSeconds,
            contentId: content.id,
            contentType: 'article',
            'source.source': activityLog.source.source,
            'source.sourceId': activityLog.source.sourceId,
            'source.sourceType': activityLog.source.sourceType,
          },
        },
        onPointsModalClosed,
      )
        .then((result) => {
          // Also log as activity if the specific event didn't yield points
          if (result.pointsCollected === 0) {
            return this.sendActivityLogged(activityLog).then((actResult) => {
              saveActivityLogDirect(activityLog).catch((err) => console.error('Failed to save article read activity:', err));
              return actResult.pointsCollected > 0 ? actResult : result;
            });
          }
          saveActivityLogDirect(activityLog).catch((err) => console.error('Failed to save article read activity:', err));
          return result;
        })
        .catch((err) => {
          console.error('Failed to send article read engagement:', err);
          onPointsModalClosed?.();
          return createEarnModelProcessResult();
        });
    },

    showPointsModal(res: EarnModelProcessResult, onPointsModalClosed?: () => void) {
      this.pointsCollected = res.pointsCollected;
      this.pointsModalData = {
        points: res.pointsCollected,
        reason: res.getRuleName(this.language.language),
        onButtonPress: () => {
          this.hidePointsModal();
          onPointsModalClosed?.();
        },
      };
      this.isPointsModalVisible = true;
    },

    hidePointsModal() {
      this.isPointsModalVisible = false;
      this.pointsModalData = null;
    },

    showPointsModalPromise(res: EarnModelProcessResult): Promise<EarnModelProcessResult> {
      if (res.pointsCollected > 0) {
        return new Promise((resolve) => {
          this.showPointsModal(res, () => {
            setTimeout(() => resolve(res));
          });
        });
      }
      return Promise.resolve(res);
    },

    get enabled(): boolean {
      return this.isEnabled;
    },
  });

  return store;
}

export type EngagementStore = ReturnType<typeof createEngagementStore>;
export const createEngagementStoreInstance = createEngagementStore;
